// pages/ProfilePage.jsx
import React, { useEffect, useState } from 'react';
import alertify from '../lib/alertifySetup';
import Seo from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { changeNickname, fetchUserGames } from '../lib/gameStorage';
import { describeMove } from '../lib/notation';
import { useLang } from '../i18n';

// 20 rozet: [id, ikon, TR başlık, TR açıklama, EN başlık, EN açıklama]
export const ALL_RIBBONS = [
  ['streak_1', '🌱', 'İlk Adım', 'İlk kez giriş yap.', 'First Step', 'Sign in for the first time.'],
  ['streak_3', '⚡', 'Kararlı Oyuncu', '3 gün aralıksız giriş yap.', 'Committed', 'Sign in 3 days in a row.'],
  ['streak_7', '🔥', 'Haftalık Seri', '7 gün aralıksız giriş yap.', 'Weekly Streak', 'Sign in 7 days in a row.'],
  ['streak_10', '💥', '10 Günlük Seri', '10 gün aralıksız giriş yap.', '10-Day Streak', 'Sign in 10 days in a row.'],
  ['streak_25', '🌋', 'Ateşli Usta', '25 gün aralıksız giriş yap.', 'On Fire', 'Sign in 25 days in a row.'],
  ['streak_50', '👑', 'Efsanevi Devamlılık', '50 gün aralıksız giriş yap.', 'Legendary Streak', 'Sign in 50 days in a row.'],
  ['games_1', '🎯', 'İlk Hamle', '1 maç tamamla.', 'First Move', 'Finish 1 game.'],
  ['games_3', '♟️', 'Çaylak Oyuncu', '3 maç tamamla.', 'Rookie', 'Finish 3 games.'],
  ['games_10', '🛡️', 'Tecrübeli Savaşçı', '10 maç tamamla.', 'Seasoned', 'Finish 10 games.'],
  ['games_25', '⚔️', 'Masa Hakimi', '25 maç tamamla.', 'Board Master', 'Finish 25 games.'],
  ['games_50', '🌟', 'Şogi Müptelası', '50 maç tamamla.', 'Shogi Addict', 'Finish 50 games.'],
  ['games_100', '💎', 'Yüzler Kulübü', '100 maç tamamla.', 'Century Club', 'Finish 100 games.'],
  ['wins_1', '🎉', 'İlk Zafer', '1 maç kazan.', 'First Win', 'Win 1 game.'],
  ['wins_3', '🏅', 'Galibiyet Serisi', '3 maç kazan.', 'Winning Run', 'Win 3 games.'],
  ['wins_5', '🏆', 'Usta Adayı', '5 maç kazan.', 'Master Candidate', 'Win 5 games.'],
  ['wins_10', '🥇', 'Strateji Dehası', '10 maç kazan.', 'Strategist', 'Win 10 games.'],
  ['wins_25', '⛩️', 'Yenilmez General', '25 maç kazan.', 'Unbeatable General', 'Win 25 games.'],
  ['mp_1', '🌐', 'Online Arena', '1 online maç oyna.', 'Online Arena', 'Play 1 online game.'],
  ['mp_3', '⚔️', 'Rakiplerin Korkulu Rüyası', '3 online maç oyna.', 'Feared Rival', 'Play 3 online games.'],
  ['mp_10', '👑', 'Arena Şampiyonu', '10 online maç oyna.', 'Arena Champion', 'Play 10 online games.'],
].map(([id, icon, trT, trD, enT, enD]) => ({ id, icon, title: { tr: trT, en: enT }, desc: { tr: trD, en: enD } }));

const T = {
  tr: {
    title: 'Profilim',
    seoDesc: 'Şogi Türkiye profilin: puanın, rozetlerin ve oyun geçmişin.',
    loginText: 'Profilini, rozetlerini, oyun geçmişini ve istatistiklerini görmek için Google hesabınla giriş yap.',
    streak: '🔥 {n} Gün Seri',
    changeNick: 'Takma adı değiştir (1 hakkın var)',
    nickChanged: 'Takma ad değiştirildi',
    newNick: 'Yeni takma ad',
    save: 'Kaydet',
    cancel: 'Vazgeç',
    nickOk: 'Takma adın güncellendi.',
    error: 'Bir hata oluştu.',
    stats: ['Toplam Puan', 'Oynanan', 'Kazanılan', 'Kaybedilen'],
    ribbons: 'Rozetler ({a} / {b})',
    done: '%{p} tamamlandı',
    locked: 'Kilitli',
    history: 'Oyun Geçmişi',
    noGames: 'Henüz kaydedilmiş oyun yok.',
    win: 'Kazandın',
    loss: 'Kaybettin',
    draw: 'Berabere',
    computer: 'Bilgisayar',
    show: 'Hamleleri gör',
    hide: 'Gizle',
  },
  en: {
    title: 'My Profile',
    seoDesc: 'Your Shogi Türkiye profile: points, badges and game history.',
    loginText: 'Sign in with your Google account to see your profile, badges, game history and stats.',
    streak: '🔥 {n}-day streak',
    changeNick: 'Change nickname (one-time)',
    nickChanged: 'Nickname changed',
    newNick: 'New nickname',
    save: 'Save',
    cancel: 'Cancel',
    nickOk: 'Your nickname was updated.',
    error: 'Something went wrong.',
    stats: ['Total Points', 'Played', 'Won', 'Lost'],
    ribbons: 'Badges ({a} / {b})',
    done: '{p}% complete',
    locked: 'Locked',
    history: 'Game History',
    noGames: 'No saved games yet.',
    win: 'Won',
    loss: 'Lost',
    draw: 'Draw',
    computer: 'Computer',
    show: 'Show moves',
    hide: 'Hide',
  },
};

export default function ProfilePage() {
  const { user, profile, loading, login } = useAuth();
  const { lang, t, locale } = useLang();
  const tx = T[lang];
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [nicknameInput, setNicknameInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedGame, setExpandedGame] = useState(null);

  useEffect(() => {
    if (!user) return;
    setGamesLoading(true);
    fetchUserGames(user.uid)
      .then(setGames)
      .catch((e) => console.error(e))
      .finally(() => setGamesLoading(false));
  }, [user, profile?.gamesPlayed]);

  const seo = <Seo title={tx.title} description={tx.seoDesc} noindex />;

  if (loading) {
    return <>{seo}<div className="container py-5 text-center text-muted">{t('common.loading')}</div></>;
  }

  if (!user) {
    return (
      <>
        {seo}
        <div className="container py-5 text-center">
          <h1 className="mb-3">{tx.title}</h1>
          <p className="text-muted mb-4">{tx.loginText}</p>
          <button type="button" className="btn btn-ink" onClick={login}>{t('auth.login')}</button>
        </div>
      </>
    );
  }

  const saveNickname = async () => {
    setSaving(true);
    try {
      await changeNickname(user.uid, nicknameInput);
      alertify.success(tx.nickOk);
      setEditing(false);
    } catch (e) {
      alertify.error(e.message || tx.error);
    } finally {
      setSaving(false);
    }
  };

  const earned = new Set((profile?.ribbons || []).map((r) => r.id));
  const earnedCount = ALL_RIBBONS.filter((r) => earned.has(r.id)).length;
  const stats = [profile?.score || 0, profile?.gamesPlayed || 0, profile?.wins || 0, profile?.losses || 0];

  return (
    <>
      {seo}
      <div className="container py-4 py-md-5">
        <div className="panel mb-4 d-flex flex-wrap align-items-center gap-3">
          {profile?.photoURL && (
            <img src={profile.photoURL} alt="" width={64} height={64} className="rounded-circle" referrerPolicy="no-referrer" />
          )}
          <div className="flex-grow-1">
            {!editing ? (
              <>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h1 className="h2 mb-0">{profile?.nickname || user.displayName}</h1>
                  <span className="tag medium">{tx.streak.replace('{n}', profile?.streak || 1)}</span>
                </div>
                <div className="text-muted small mb-2">{user.email}</div>
                {!profile?.nicknameChanged ? (
                  <button type="button" className="btn btn-sm btn-soft" onClick={() => { setNicknameInput(profile?.nickname || ''); setEditing(true); }}>
                    {tx.changeNick}
                  </button>
                ) : (
                  <span className="tag">{tx.nickChanged}</span>
                )}
              </>
            ) : (
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <input
                  className="form-control"
                  style={{ maxWidth: 260 }}
                  value={nicknameInput}
                  onChange={(e) => setNicknameInput(e.target.value)}
                  maxLength={30}
                  placeholder={tx.newNick}
                  aria-label={tx.newNick}
                  autoFocus
                />
                <button type="button" className="btn btn-ink" onClick={saveNickname} disabled={saving || !nicknameInput.trim()}>{tx.save}</button>
                <button type="button" className="btn btn-soft" onClick={() => setEditing(false)} disabled={saving}>{tx.cancel}</button>
              </div>
            )}
          </div>
        </div>

        <div className="row g-3 mb-4">
          {stats.map((v, i) => (
            <div className="col-6 col-md-3" key={tx.stats[i]}>
              <div className="stat-tile h-100 text-center">
                <b>{v}</b>
                <span>{tx.stats[i]}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>{tx.ribbons.replace('{a}', earnedCount).replace('{b}', ALL_RIBBONS.length)}</span>
            <span className="tag easy">{tx.done.replace('{p}', Math.round((earnedCount / ALL_RIBBONS.length) * 100))}</span>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {ALL_RIBBONS.map((r) => {
                const isEarned = earned.has(r.id);
                return (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={r.id}>
                    <div
                      className="border rounded-3 p-3 h-100 d-flex align-items-center gap-3"
                      style={{ background: isEarned ? '#fff' : 'var(--surface-2)', opacity: isEarned ? 1 : 0.55, filter: isEarned ? 'none' : 'grayscale(90%)' }}
                    >
                      <div className="fs-1">{r.icon}</div>
                      <div>
                        <div className="fw-bold small">
                          {r.title[lang]} {!isEarned && <span className="tag ms-1" style={{ fontSize: '0.65rem' }}>🔒 {tx.locked}</span>}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.desc[lang]}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">{tx.history}</div>
          <div className="card-body p-0">
            {gamesLoading ? (
              <div className="p-3 text-muted small">{t('common.loading')}</div>
            ) : games.length === 0 ? (
              <div className="p-3 text-muted small">{tx.noGames}</div>
            ) : (
              <ul className="list-group list-group-flush">
                {games.map((g) => (
                  <li key={g.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <span className={`tag me-2 ${g.result === 'win' ? 'easy' : g.result === 'loss' ? 'hard' : ''}`}>
                          {tx[g.result] || g.result}
                        </span>
                        <span className="fw-bold me-2">vs. {g.opponentNickname || tx.computer}</span>
                        <span className="small text-muted">
                          {g.playedAt?.toDate ? g.playedAt.toDate().toLocaleString(locale) : '—'}
                          {' · '}{t('common.moves', { n: g.moveCount })}
                        </span>
                      </div>
                      <button type="button" className="btn btn-sm btn-soft" onClick={() => setExpandedGame(expandedGame === g.id ? null : g.id)}>
                        {expandedGame === g.id ? tx.hide : tx.show}
                      </button>
                    </div>
                    {expandedGame === g.id && (
                      <ol className="small mt-2 mb-0 ps-3" style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {g.history?.map((m, i) => (
                          <li key={i}>
                            <span className={m.color === 'sente' ? 'fw-semibold' : 'text-danger fw-semibold'}>
                              {m.color === 'sente' ? t('common.sente') : t('common.gote')}
                            </span>
                            {' — '}{describeMove(m, lang)}
                          </li>
                        ))}
                      </ol>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
